import { AuthTokens, AuthServiceError, AuthErrorType, User } from '../types/auth';
import { UserRole } from '../types/roles';
import { apiService } from './apiService';
import { clearLegacyAuthStorage } from '../utils/authSession';

interface AuthService {
  login(identifier: string, password: string): Promise<{ user: User; tokens: AuthTokens }>;
  refreshToken(refreshToken: string): Promise<AuthTokens>;
  logout(): Promise<void>;
  signup(data: SignupData): Promise<{ user: User; tokens: AuthTokens }>;
}

interface SignupData {
  name: string;
  email?: string;
  whatsappPhone?: string;
  password: string;
}

interface LoginApiUser {
  id: number | string;
  email?: string | null;
  whatsappNumber?: string | null;
  firstName?: string;
  lastName?: string;
  role?: string;
}

interface LoginApiResponse {
  access_token?: string;
  refresh_token?: string;
  user: LoginApiUser;
}

interface RefreshApiResponse {
  access_token?: string;
  refresh_token?: string;
}

interface SignupApiResponse {
  access_token?: string;
  refresh_token?: string;
  user?: LoginApiUser;
}

const toUserRole = (role?: string): UserRole => {
  return role === UserRole.ADMIN ? UserRole.ADMIN : UserRole.USER;
};

const toUser = (raw: LoginApiUser): User => ({
  id: String(raw.id),
  email: raw.email ?? null,
  whatsappNumber: raw.whatsappNumber ?? null,
  firstName: raw.firstName ?? '',
  lastName: raw.lastName ?? '',
  role: toUserRole(raw.role),
  isAuthenticated: true,
});

class AuthServiceImpl implements AuthService {
  async login(identifier: string, password: string): Promise<{ user: User; tokens: AuthTokens }> {
    try {
      const response = await apiService.login({ identifier, password });
      const payload = response as LoginApiResponse;
      const accessToken = payload.access_token || '';
      const refreshToken = payload.refresh_token || accessToken;

      const tokens: AuthTokens = {
        accessToken,
        refreshToken,
      };

      const user = toUser(payload.user);
      localStorage.setItem('user', JSON.stringify(user));
      clearLegacyAuthStorage();

      return { user, tokens };
    } catch (error) {
      throw new AuthServiceError(AuthErrorType.INVALID_CREDENTIALS, 'Invalid email/phone or password');
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    try {
      const payload = refreshToken ? { refresh_token: refreshToken } : {};
      const response = await apiService.post<RefreshApiResponse>('/auth/refresh', payload);
      const accessToken = response.data.access_token || '';
      const refreshedToken = response.data.refresh_token || refreshToken || accessToken;
      clearLegacyAuthStorage();

      return {
        accessToken,
        refreshToken: refreshedToken,
      };
    } catch (error) {
      throw new AuthServiceError(AuthErrorType.REFRESH_FAILED);
    }
  }

  async logout(): Promise<void> {
    try {
      await apiService.logout();
    } catch {
      // Keep local logout deterministic even if server logout fails.
    }

    apiService.removeAuthToken();
    clearLegacyAuthStorage();
    localStorage.removeItem('user');
  }

  async signup(data: SignupData): Promise<{ user: User; tokens: AuthTokens }> {
    const [firstName, ...rest] = data.name.trim().split(/\s+/);
    const lastName = rest.join(' ');

    const response = await apiService.request<SignupApiResponse>({
      method: 'POST',
      url: '/auth/signup',
      data: {
        email: data.email,
        whatsapp: data.whatsappPhone,
        first_name: firstName || '',
        last_name: lastName,
        password: data.password,
        user_type: 'USER',
      },
    });

    const payload = response.data;
    if (payload?.user) {
      const user = toUser(payload.user);
      const tokens: AuthTokens = {
        accessToken: payload.access_token || '',
        refreshToken: payload.refresh_token || payload.access_token || '',
      };
      return { user, tokens };
    }

    throw new AuthServiceError(
      AuthErrorType.AUTHENTICATION,
      'Signup completed. Verify your account and sign in.'
    );
  }
}

export const authService = new AuthServiceImpl();
